import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { fetchApiInfo } from "@/api/meta";
import { usePermission } from "@/auth/usePermission";
import { NAV_SECTIONS, type NavItem } from "@/nav/sections";
import { useAuthStore } from "@/store/authStore";

const DEFAULT_COLOR = "#546e7a";

// All permissions referenced by the nav definition. Order is stable so the
// usePermission calls below stay in the same React hook position across renders.
const ALL_PERMS = Array.from(
  new Set(
    NAV_SECTIONS
      .flatMap((s) => s.items.map((i) => i.permission ?? ""))
      .filter((p) => p && p !== "__god__"),
  ),
).sort();

export function HomePage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isGod = Boolean(useAuthStore((s) => s.me)?.is_god);
  const hasTenant = Boolean(useAuthStore((s) => s.me)?.tenant_id);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["api-info"],
    queryFn: fetchApiInfo,
    refetchInterval: 60_000,
    retry: 1,
  });

  // Call usePermission for every known nav permission in stable order so the
  // hook count never changes between renders.
  const permResults = ALL_PERMS.map((code) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    usePermission(code),
  );
  const permMap: Record<string, boolean> = {};
  ALL_PERMS.forEach((code, i) => {
    permMap[code] = permResults[i] ?? false;
  });

  const allowed = (item: NavItem): boolean => {
    if (item.requiresTenant && !hasTenant) return false;
    if (!item.permission) return true;
    if (item.permission === "__god__") return isGod;
    return permMap[item.permission] ?? false;
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t("home.welcome")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
          {t("home.subtitle")}
        </Typography>
      </Box>

      <Card variant="outlined">
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Typography variant="overline" color="text.secondary" sx={{ mr: 2 }}>
            {t("home.apiStatus")}
          </Typography>
          {isLoading && (
            <Skeleton variant="text" width={200} sx={{ display: "inline-block" }} />
          )}
          {isError && (
            <Alert severity="error" sx={{ display: "inline-flex", py: 0 }}>
              {t("home.apiUnreachable")}
            </Alert>
          )}
          {data && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexWrap: "wrap", display: "inline-flex" }}
            >
              <Chip
                size="small"
                color="success"
                label={t("home.apiConnected", { version: data.version })}
              />
              <Chip size="small" variant="outlined" label={`env: ${data.env}`} />
            </Stack>
          )}
        </CardContent>
      </Card>

      {NAV_SECTIONS.map((section) => {
        const tiles = section.items.filter(allowed);
        if (tiles.length === 0) return null;
        return (
          <Box key={section.id}>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.8,
                color: "text.secondary",
                mb: 1,
                display: "block",
              }}
            >
              {t(`nav.section.${section.id}`)}
            </Typography>
            <Grid container spacing={2}>
              {tiles.map((tile) => {
                const tileColor = tile.color ?? DEFAULT_COLOR;
                const description = t(`home.tile.${tile.key}`, { defaultValue: "" });
                const isExternal = tile.to.startsWith("/api/") || /^https?:/.test(tile.to);
                return (
                  <Grid key={tile.key} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: isDark
                          ? alpha(tileColor, 0.45)
                          : alpha(tileColor, 0.35),
                        bgcolor: isDark
                          ? alpha(tileColor, 0.08)
                          : alpha(tileColor, 0.04),
                        transition:
                          "transform 120ms, box-shadow 120ms, border-color 120ms",
                        "&:hover": {
                          borderColor: tileColor,
                          transform: "translateY(-2px)",
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardActionArea
                        component={isExternal ? "a" : RouterLink}
                        {...(isExternal
                          ? { href: tile.to, target: "_blank", rel: "noopener" }
                          : { to: tile.to })}
                      >
                        <CardContent>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: isDark
                                  ? alpha(tileColor, 0.25)
                                  : alpha(tileColor, 0.15),
                                color: isDark
                                  ? alpha(tileColor, 0.95)
                                  : tileColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": { fontSize: 28 },
                              }}
                            >
                              {tile.icon}
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {t(`nav.${tile.key}`)}
                              </Typography>
                              {description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {description}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      })}
    </Stack>
  );
}
